//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { FulfillmentType } from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import { CampaignRequest } from '@apis/campaign-requests/shared/CampaignRequests';
import { CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/CampaignsTrackingId';
import HelpIcon from '@components/atoms/help-icon/HelpIcon';
import Icon from '@components/atoms/icon/Icon';
import { BodyText, CaptionErrorText } from '@components/atoms/text/Text';
import { FormFieldController } from '@components/form/field-controller/FormFieldController';
import { RadioGroup } from '@components/primitives/radio-group/RadioGroup';

const radioItems = [
  {
    value: 'shipped',
    label: 'Shipped products',
    trackingId:
      CAMPAIGNS_TRACKING_ID.CREATE_CAMPAIGN_DELIVERY_SHIPPED_RADIO_ITEM,
  },
  {
    value: 'deals',
    label: 'Coupon codes',
    trackingId:
      CAMPAIGNS_TRACKING_ID.CREATE_CAMPAIGN_DELIVERY_COUPON_RADIO_ITEM,
  },
];

interface CampaignRequestRadioFieldProps {
  isDisabled?: boolean;
}

export const CampaignRequestDeliveryField = ({
  isDisabled,
}: CampaignRequestRadioFieldProps) => {
  const { control, setValue } = useFormContext<CampaignRequest>();

  const handleDeliveryMethodChange = (value: FulfillmentType) => {
    setValue('comments.shipping', undefined);

    setValue('fulfillmentOptions', {
      fulfillmentType: value,
      boxType: undefined,
      whoShipsProduct: undefined,
      emails: undefined,
      riskManagement: undefined,
      codeScope: undefined,
      clientCodes: undefined,
      clientCodesSupplied: undefined,
      codeSupplier: undefined,
      codeType: undefined,
      codeValue: undefined,
      redemptionType: undefined,
      retailers: undefined,
    });
  };

  return (
    <Container>
      <Title>
        <BodyText>Delivery method</BodyText>
        <HelpIcon helpText='Select how you want members to receive products. Ship products directly to members or send codes to members they can redeem to purchase products themselves.' />
      </Title>
      <FormFieldController
        control={control}
        name='fulfillmentOptions.fulfillmentType'
        rules={{ required: 'Please select a delivery method.' }}
      >
        {({ disabled, error, onChange, value, ...props }) => (
          <>
            <StyledRadioGroup
              {...props}
              disabled={isDisabled ?? disabled}
              onValueChange={handleDeliveryMethodChange}
              value={value}
            >
              {radioItems.map((item) => (
                <RadioGroupItemWrapper key={item.value}>
                  <StyledRadioGroupItem
                    id={item.value}
                    trackingId={item.trackingId}
                    value={item.value}
                  >
                    <RadioGroup.Indicator />
                  </StyledRadioGroupItem>
                  <StyledRadioGroupLabel htmlFor={item.value}>
                    <BodyText>{item.label}</BodyText>
                  </StyledRadioGroupLabel>
                </RadioGroupItemWrapper>
              ))}
            </StyledRadioGroup>
            {error && (
              <ErrorText>
                <StyledIcon name='error' />
                <CaptionErrorText>{error.message}</CaptionErrorText>
              </ErrorText>
            )}
          </>
        )}
      </FormFieldController>
    </Container>
  );
};

const Container = styled.div`
  margin-top: ${({ theme }) => theme.spacings[4]};
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacings[2]};
`;

const StyledRadioGroup = styled(RadioGroup)`
  margin-top: ${({ theme }) => theme.spacings[1]};
  display: flex;
  gap: ${({ theme }) => theme.spacings[2]};
`;

const RadioGroupItemWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledRadioGroupItem = styled(RadioGroup.Item)`
  margin-right: ${({ theme }) => theme.spacings[1]};
`;

const StyledRadioGroupLabel = styled(RadioGroup.Label)`
  display: flex;
  align-items: center;
`;

const ErrorText = styled.div`
  display: flex;
  align-items: center;
`;

const StyledIcon = styled(Icon)(
  ({ theme }) => `
    margin-right: ${theme.spacings[1]};
    color: ${theme.colors.aliases.error};
    height: calc(${theme.spacings[1]} * 3);
    width: calc(${theme.spacings[1]} * 3);
`,
);
